<script setup lang="ts">
import { useRoute } from 'vue-router';
import { useForm } from 'vee-validate';
import * as yup from '@/helpers/yup-distilled';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import InputOtp from 'primevue/inputotp';

import { router, getNextRouteFromNewSigninState } from '@/helpers';
import { useAuthStore } from '@/stores';
import { getFriendlyErrorMessage } from '@/helpers';

const { VITE_NO_REMEMBER_ME_SYSROOTS } = import.meta.env;

const noRememberMeSysroots = VITE_NO_REMEMBER_ME_SYSROOTS
	? VITE_NO_REMEMBER_ME_SYSROOTS.split('|')
	: [];

const noRememberMe = noRememberMeSysroots
	.map((sysroot: string) => location.pathname.startsWith(sysroot))
	.some((t: boolean) => t);

const route = useRoute();
const authStore = useAuthStore();

// redirect to login if no auth token
if (!authStore.authToken) {
	router.push(import.meta.env.VITE_SIGNIN_BASEURL);
}

const schema = yup.object().shape({
	passcode: yup.string().required('Please enter your passcode'),
	rememberMe: yup.boolean()
});

const { defineField, errors, isSubmitting, handleSubmit } = useForm({
	validationSchema: schema
});

const [passcode] = defineField('passcode');
const [requestmfacookie] = defineField('requestmfacookie');

const onSubmit = handleSubmit((values, { setErrors }) => {
	const { passcode, requestmfacookie } = values;

	return authStore
		.submitPasscode(passcode, requestmfacookie)
		.then(() => {
			// redirect to previous url or default to home page
			router.replace(
				getNextRouteFromNewSigninState(authStore.signInState, route)
			);
		})
		.catch(async (error) => {
			const errorOutput = await getFriendlyErrorMessage(error);
			setErrors({ apiError: errorOutput.message });
		});
});

const passCodeValidity = 10; // replace with correct value
</script>

<template>
	<form @submit="onSubmit">
		<div class="p-fluid login-small-form">
			<div class="p-field">
				When you receive the one-time passcode, please enter it below.
			</div>
			<div class="p-field mt-1">
				The passcode will only be valid for
				{{ passCodeValidity }} minutes.
			</div>
			<div>
				<div class="flex flex-row justify-content-center">
					<label class="font-semibold" for="passcode"
						>Passcode:</label
					>
				</div>
				<div class="flex flex-row justify-content-center">
					<InputOtp
						autofocus
						v-model="passcode"
						:invalid="!!errors.passcode"
						:length="8"
						:pt="{
							input: {
								root: {
									style: 'width: 2.5rem; text-align: center'
								}
							}
						}"
					/>
				</div>
			</div>
			<div v-if="errors.passcode" class="p-error justify-content-center">
				{{ errors.passcode }}&nbsp;
			</div>
			<div class="field-checkbox mt-2 justify-content-center"
			v-if="!noRememberMe">
				<Checkbox v-model="requestmfacookie" binary></Checkbox>
				<label for="requestmfacookie">Remember me on this device</label>
			</div>
		</div>
		<div class="p-fluid">
			<Button
				:disabled="isSubmitting"
				label="Submit"
				type="submit"
			></Button>
			<div v-if="errors.apiError" class="p-error">
				{{ errors.apiError }}
			</div>
		</div>
	</form>
</template>
